import React, { useState, useCallback, useEffect } from "react";
import { Text, Button, Select, FormControl, FormLabel, Tooltip } from "@chakra-ui/react";
import { CreatableSelect } from "chakra-react-select";
import IssbContainer from "./IssbContainer";
import SourcesListPanel from "./Sources/SourcesListPanel";
import PageHeader from "components/text/PageHeader";
import { H2, H3 } from "components/text/headings";
import { KB_FOLDER } from "api/common/fileinfo";
import { Card } from "components/Card";
import useAirUserRoles from "utils/AuthUtils";
import { useAuth } from "context";
import { useToast } from "@chakra-ui/react";
import { useRerender } from "rerenderer";
import { useQuery } from "react-query";
import { organizationService } from "api/organization-administration";
import { deepCopy } from "utils";
import PropInput from "components/PropInput";
import { getPromptList, getPrompt } from "api/ai/ai.service";

/**
 * AI Custom Settings (stored on Organization)
 */
export default function AISettingsPage() {
	const { isOrgAdmin, isESGAdmin } = useAirUserRoles();

	const { getAccessTokenSilently } = useAuth();
	const toast = useToast();

	const { rerender } = useRerender();
	const [serverCopySnapshotJson, setServerCopySnapshotJson] = useState(null);
	const { user } = useAuth();

	const org_id = user?.org_id;
	const { data: orgData, isLoading } = useQuery({
		queryKey: ["org", org_id],
		queryFn: () => getAccessTokenSilently().then(() => organizationService.get(org_id)),
		enabled: !!org_id,
	});
	useEffect(() => {
		if (orgData) {
			if ( ! orgData.settings) orgData.settings = {};
			setServerCopySnapshotJson(JSON.stringify(orgData));
			rerender();
		}
	}, [orgData, rerender]);
	const isModified = serverCopySnapshotJson && JSON.stringify(orgData) !== serverCopySnapshotJson;

	// Save handler
	const [isSaving, setIsSaving] = useState(false);
	const handleSave = useCallback(() => {
		if (!orgData) return;
		setIsSaving(true);
		organizationService
			.update(org_id, orgData)
			.then((data) => {
				setIsSaving(false);
				setServerCopySnapshotJson(JSON.stringify(data));
				toast({
					title: "Organization updated",
					description: `Organization ${data.name} has been updated successfully.`,
					status: "success",
					duration: 5000,
					isClosable: true,
				});
			})
			.catch((error) => {
				setIsSaving(false);
				toast({
					title: "Error updating organization",
					description: error?.message || "An error occurred while updating the organization.",
					status: "error",
					duration: 5000,
					isClosable: true,
				});
			});
	}, [org_id, orgData, toast, setIsSaving]);

	if (!isESGAdmin) {
		return <Text>You are not authorized to access this system!</Text>;
	}

	return (
		<IssbContainer>
			<PageHeader title={"AI Settings for " + orgData?.name || org_id} />

			<Card bg="blue.50" p={4} mb={4} borderLeft="4px solid" borderColor="blue.400">
				<Text fontSize="sm" color="blue.800" fontWeight="bold" mb={1}>1M Context Support</Text>
				<Text as="ul" fontSize="sm" color="blue.800" pl={4} styleType="disc" listStyleType="disc">
					<li>Select <strong>claude-opus-4.6</strong> as the model (default: claude-4.5)</li>
					<li>Increase <em>Max Thread Characters</em> to 500,000+ (default: 50,000)</li>
					<li>Increase <em>Max Prompt+Context Characters</em> to 500,000+ (default: 64,000)</li>
					<li>Increase <em>RAG Slices Limit</em> to 50+ (default: 10 files / 20 questions)</li>
					<li>Increase <em>Max Canvas Text Characters</em> to 100,000+ (default: 20,000)</li>
				</Text>
			</Card>

			<PropInput
				label="AI: Custom Prompt Snippet"
				type="textarea"
				help="Optional AI customisation - inserted into the main prompt. Can be used for e.g. tone of voice"
				prop="aiCustomPromptSnippet"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<AIOverrides orgData={orgData} rerender={rerender} />

			<Button variant="primary" onClick={handleSave} isLoading={isLoading} disabled={!isModified}>Save</Button>
			<H2>Knowledge Base Sources (cross-organisation)</H2>
			<SourcesListPanel tables={[KB_FOLDER]} hideAIReviewButton={true} />
		</IssbContainer>
	);
}


function AIOverrides({ orgData }) {
	const { getAccessTokenSilently } = useAuth();
	const { rerender } = useRerender();
	const promptOverrides = orgData?.settings?.prompt_overrides || {};
	const [promptOriginals, setPromptOriginals] = useState({});

	const { data: promptList } = useQuery({
		queryKey: ["promptList"],
		queryFn: () => getAccessTokenSilently().then(() => getPromptList()),
	});
	const [selectedPrompt, setSelectedPrompt] = useState(null);
   const { data: selectedPromptData } = useQuery({
		queryKey: ["selectedPrompt", selectedPrompt],
		queryFn: () => getAccessTokenSilently().then(() => getPrompt(selectedPrompt)),
		enabled: !!selectedPrompt,
		onSuccess: (data) => { // Note: only need to do this on 1st load
			promptOriginals[selectedPrompt] = data;
			setPromptOriginals(promptOriginals);
			rerender();
		},
	});
	const setPrompt = (e) => {
		const prompt = e.target.value;
		promptOverrides[selectedPrompt] = prompt;
		if (prompt==null || prompt==="") delete promptOverrides[selectedPrompt];
		if ( ! orgData.settings) orgData.settings = {};
		orgData.settings.prompt_overrides = promptOverrides;
		rerender();
	};

	const allPrompts = Object.assign({}, promptOriginals, promptOverrides);
	const isPromptOverridden = (promptName) => {
		return !! promptOverrides[promptName];
	};
	console.log("allPrompts", allPrompts);
	const CHECKS_LIST = [
		"schema", "factcheck", "objectivity", "cite_tags", "check_canvas_edit", "user_rep"
	];
	return (
		<Card>
			<H2>AI Overrides</H2>

			<PropInput
				options={CHECKS_LIST}
			    type="select"
				label="Checks: Switched Off"
				prop="checks_off"
				multiple={true}
				item={orgData?.settings}
				onChange={rerender}
			/>

			<PropInput
				label="RAG slices (i.e. pages) limit"
				type="number"
				help="Override the default 10 / 20 slices (pages) limit"
				prop="rag_slices_limit"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="AI task max retries"
				type="number"
				help="Max auto-requeues for rate-limited AI Review tasks before giving up (default 5). Lower this if Bedrock throttling is burning your daily token quota."
				prop="task_max_retries"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="Max total prompt+context characters"
				type="number"
				help="Override the default 64k character limit"
				prop="max_total_prompt_context_characters"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="Max total thread-messages characters"
				type="number"
				help="Override the default 50k character limit"
				prop="max_thread_characters"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="Report writer: Max canvas text (report content html) characters in LLM call"
				type="number"
				help="Override the default 20k canvas text limit"
				prop="max_canvas_text_in_llm_call_characters"
				item={orgData?.settings}
				onChange={rerender}
			/>			
			<PropInput
				label="Agent loop: max steps"
				type="number"
				help="Override the default 6 steps"
				prop="agent_loop_max_steps"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="Agent loop: Slow mode: max steps"
				type="number"
				help="Override the default 20 max steps"
				prop="agent_loop_slow_mode_max_steps"
				item={orgData?.settings}
				onChange={rerender}
			/>
			{/* only find_replace supported at least for now (currently its more reliable) <PropInput
				label="Canvas Edits - Format"
				type="select"
				help="Override the default find/replace"
				options={["find_replace", "unidiff"]}
				prop="canvas_edits_format"
				item={orgData?.settings}
				onChange={rerender}
			/> */}
			
			<PropInput
				label="Model Provider"
				type="select"
				options={["bedrock","openai"]}
				prop="llm_provider"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="Model Name"
				type="select"
				help="claude-opus-4.6 supports 1M context. To utilize it, increase max thread/prompt characters to 500k+ and RAG slices to 50+"
				/* https://platform.openai.com/docs/api-reference/models/list?utm_source=chatgpt.com */
				options={["claude-4.5", "claude-opus-4.6", "claude-opus-4.6-au", "claude-opus-4.5", "claude-4","claude-3.5","gpt-5","gpt-5-pro","gpt-4.1","gpt-5-mini","gpt-5-nano"]}
				prop="llm_model_name"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="LangFuse Tracing?"
				type="checkbox"
				help="Switch on/off LangFuse tracing (we have a limited usage budget for this, so only switch on when doing tracking an issue)"
				prop="is_tracing"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<PropInput
				label="Adhoc Feature Flags"
				type="text"
				help="For linking up with custom shims in the backend"
				prop="adhoc_feature_flags"
				item={orgData?.settings}
				onChange={rerender}
			/>


			<H3>Web Search</H3>
			<PropInput
				label="Enabled Components"
				type="select"
				multiple={true}
				help="Which AI components can use web search. Empty = disabled everywhere."
				options={["chat", "report_writer", "question_answer"]}
				prop="web_search_enabled_components"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<DomainTagsInput
				label="Include Domains"
				help="Whitelist of domains to search. Leave empty for no restriction. E.g. issb.org, sec.gov"
				prop="web_search_domains_include"
				item={orgData?.settings}
				onChange={rerender}
			/>
			<DomainTagsInput
				label="Exclude Domains"
				help="Blacklist of domains to exclude from search results."
				prop="web_search_domains_exclude"
				item={orgData?.settings}
				onChange={rerender}
			/>

			<H3>Prompt Overrides</H3>
			<Select value={selectedPrompt} onChange={(e) => setSelectedPrompt(e.target.value)}>
				{promptList?.map((promptName) => (
					<option key={promptName} value={promptName}><span style={{fontWeight: isPromptOverridden(promptName) ? "bold" : "normal"}}>{isPromptOverridden(promptName) ? "*" : ""}{promptName}</span></option>
				))}
			</Select>
			<PropInput
				label="Prompt"
				type="textarea"
				prop={selectedPrompt}
				item={allPrompts}
				onChange={setPrompt}				
			/>
			{isPromptOverridden(selectedPrompt) &&
				<Button size="sm" variant="secondary" onClick={() => setPrompt({target: {value: null}})}>Reset {selectedPrompt} to Standard</Button>}
		</Card>
	)
}


function DomainTagsInput({ label, help, prop, item, onChange }) {
	const value = (item?.[prop] || []).map(d => ({ label: d, value: d }));
	const handleChange = (selected) => {
		if (!item) return;
		item[prop] = (selected || []).map(s => s.value);
		onChange();
	};
	return (
		<FormControl mb={3}>
			<FormLabel>
				{label}
				{help && <Tooltip label={help} placement="top"><Text as="span" ml={1} cursor="help" color="gray.400">?</Text></Tooltip>}
			</FormLabel>
			<CreatableSelect
				isMulti
				value={value}
				onChange={handleChange}
				placeholder="Type a domain and press Enter..."
				noOptionsMessage={() => "Type a domain..."}
				formatCreateLabel={(input) => `Add "${input}"`}
			/>
		</FormControl>
	);
}